import React from "react";
import { Box, Group, Pagination, Select, Text } from "@mantine/core";
import styles from "./sales.module.css";

interface SalesPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  itemLabel: string;
}

const PAGE_SIZE_OPTIONS = ["25", "50", "100"];

/**
 * User-controlled pagination footer, mirroring the Clients list (StandardTable):
 * a "Showing X–Y of Z" summary, a page-size Select, and Mantine `Pagination`.
 * Client-side slicing is fine for v1 since the rows are already date-ranged.
 */
export default function SalesPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  itemLabel,
}: SalesPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <Box className={styles.paginationFooter}>
      <Group gap="md" wrap="nowrap">
        <Text className={styles.paginationInfo}>
          {total === 0
            ? `No ${itemLabel}`
            : `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()} ${itemLabel}`}
        </Text>
        <Select
          size="xs"
          w={90}
          value={pageSize.toString()}
          onChange={(v) => v && onPageSizeChange(Number(v))}
          data={PAGE_SIZE_OPTIONS}
          allowDeselect={false}
          aria-label="Rows per page"
        />
      </Group>
      <Pagination
        size="sm"
        total={totalPages}
        value={page}
        onChange={onPageChange}
        color="var(--brand-teal)"
      />
    </Box>
  );
}
